import { QueryClientProvider } from "@tanstack/react-query";
import NavbarHeader from "components/navbar/navbar-header";
import { AppSidebar } from "components/navbar/sidebar";
import { SidebarInset } from "components/ui/sidebar";
import {
  createBrowserRouter,
  Navigate,
  RouterProvider,
  useLocation,
} from "react-router-dom";
import { queryClient } from "../../src/config/query-client";
import { LazyLoadErrorBoundary } from "../components/error-boundary/LazyLoadErrorBoundary";
import { LoadingSpinner } from "../components/loading/LoadingSpinner";
import PageError from "../components/page-error";
import { PrivateRoute } from "./private-route";
import ScrollToTop from "./scroll-to-top";

import { ReactNode, Suspense, lazy, ComponentType } from "react";
import { excludedRoutes } from "./public-route-map";
import { AuthDataProvider } from "@/context/auth-data-provider";
import { SubstitutionWatcher } from "@/components/substitution/substitution-watcher";

const TIMEOUT_DURATION = 15000;

const PRICE_MARKDOWN_LEGACY_PREFIX = "/planejamento-comercial/price-markdown";

type LazyComponent = { default: ComponentType<any> };

const createErrorComponent = (message: string): LazyComponent => ({
  default: () => <PageError status={500} message={message} />,
});

const withRetry = (
  importFunc: () => Promise<{ default: ComponentType<any> }>,
  retries = 3
): (() => Promise<{ default: ComponentType<any> }>) => {
  return () => {
    const timeoutPromise = new Promise<never>((_, reject) => {
      setTimeout(
        () => reject(new Error("Timeout ao carregar componente")),
        TIMEOUT_DURATION
      );
    });

    const importPromise = importFunc()
      .then((module) => ({ default: module.default || module }))
      .catch((error: Error) => {
        if (retries > 0) {
          console.warn(
            `Erro ao carregar componente, tentando novamente... (${retries} tentativas restantes)`,
            error
          );
          return new Promise((resolve) => {
            setTimeout(
              () => resolve(withRetry(importFunc, retries - 1)()),
              1500
            );
          });
        }
        console.error("Erro fatal ao carregar componente:", error);
        return createErrorComponent(
          "Erro ao carregar página. Tente atualizar a página ou faça login novamente."
        );
      }) as Promise<{ default: ComponentType<any> }>;

    return Promise.race([importPromise, timeoutPromise]).catch((error) => {
      console.error("Erro ao carregar componente:", error);
      return createErrorComponent(
        error.message === "Timeout ao carregar componente"
          ? "Tempo limite excedido. Verifique sua conexão e tente novamente."
          : "Erro ao carregar página. Tente atualizar a página ou faça login novamente."
      );
    });
  };
};

const RoutesIndex = lazy(
  withRetry(() => import("../pages/conciliation/index.route"))
);
const Informations = lazy(
  withRetry(() => import("../pages/conciliation/informations"))
);
const SignIn = lazy(
  withRetry(() =>
    import("../pages/signIn").then((module) => ({ default: module.SignIn }))
  )
);
const Dashboard = lazy(withRetry(() => import("../../src/pages/home")));
const RoutesIndexTax = lazy(withRetry(() => import("@/pages/tax/index.route")));
const Redirect = lazy(withRetry(() => import("@/pages/conciliation/redirect")));
const RoutesIndexFinancial = lazy(
  withRetry(() => import("@/pages/financial/index.route"))
);
const UserPage = lazy(withRetry(() => import("../pages/users")));
const RoutesIndexIt = lazy(withRetry(() => import("../pages/it/index.route")));
const RoutesIndexStoreBackOffice = lazy(
  withRetry(() => import("../pages/store-back-office/index.route"))
);
const RoutesIndexCrm = lazy(
  withRetry(() => import("../pages/crm/index.route"))
);
const RoutesIndexRh = lazy(withRetry(() => import("../pages/rh/index.route")));
const RoutesIndexBusinessPlanning = lazy(
  withRetry(() => import("../pages/business-planning/index.route"))
);
const RoutesIndexPriceRemark = lazy(
  withRetry(() => import("../pages/price-remark/index.route"))
);
const RoutesIndexRemarcacaoDePreco = lazy(
  withRetry(() => import("../pages/remarcacao-de-preco/index.route"))
);
const GroupsPage = lazy(
  withRetry(() => import("@/pages/settings/groups-settings"))
);

export const PriceMarkdownLegacyRedirect = () => {
  const { pathname, search, hash } = useLocation();

  const subPath = pathname.slice(PRICE_MARKDOWN_LEGACY_PREFIX.length).replace(/^\//, "");

  return (
    <Navigate to={`/remarcacao-de-preco/${subPath}${search}${hash}`} replace />
  );
};

export const LayoutWrapper = ({ children }: { children: ReactNode }) => {
  const location = useLocation();

  const isExcluded = excludedRoutes.some((path) =>
    location.pathname.startsWith(path)
  );

  if (isExcluded) {
    return (
      <div className="flex flex-col w-full">
        <NavbarHeader />
        <div className="flex-1">
          <Suspense fallback={<LoadingSpinner />}>{children}</Suspense>
        </div>
      </div>
    );
  }

  return (
    <PrivateRoute>
      <AuthDataProvider>
        <SubstitutionWatcher />
        <AppSidebar />
        <SidebarInset>
          <ScrollToTop />
          <NavbarHeader />
          <div className="flex-1">
            <LazyLoadErrorBoundary>
              <Suspense fallback={<LoadingSpinner />}>{children}</Suspense>
            </LazyLoadErrorBoundary>
          </div>
        </SidebarInset>
      </AuthDataProvider>
    </PrivateRoute>
  );
};

const router = createBrowserRouter([
  {
    path: "/",
    element: (
      <LayoutWrapper>
        <Dashboard />
      </LayoutWrapper>
    ),
  },
  {
    path: "/conciliacao/*",
    element: (
      <LayoutWrapper>
        <RoutesIndex />
      </LayoutWrapper>
    ),
  },
  {
    path: "/ti/*",
    element: (
      <LayoutWrapper>
        <RoutesIndexIt />
      </LayoutWrapper>
    ),
  },
  {
    path: "/financeiro/*",
    element: (
      <LayoutWrapper>
        <RoutesIndexFinancial />
      </LayoutWrapper>
    ),
  },
  {
    path: "/fiscal/*",
    element: (
      <LayoutWrapper>
        <RoutesIndexTax />
      </LayoutWrapper>
    ),
  },
  {
    path: "/tax/*",
    element: (
      <LayoutWrapper>
        <RoutesIndexTax />
      </LayoutWrapper>
    ),
  },
  {
    path: "/retaguarda-de-loja/*",
    element: (
      <LayoutWrapper>
        <RoutesIndexStoreBackOffice />
      </LayoutWrapper>
    ),
  },
  {
    path: "/etiqueta-remarcados/*",
    element: (
      <LayoutWrapper>
        <RoutesIndexPriceRemark />
      </LayoutWrapper>
    ),
  },

  // {
  //   path: "/crm/*",
  //   element: (
  //     <LayoutWrapper>
  //       <RoutesIndexCrm />
  //     </LayoutWrapper>
  //   ),
  // },
  {
    path: "/crm/*",
    element: (
      <LayoutWrapper>
        <RoutesIndexCrm />
      </LayoutWrapper>
    ),
  },
  {
    path: "/rh/*",
    element: (
      <LayoutWrapper>
        <RoutesIndexRh />
      </LayoutWrapper>
    ),
  },
  {
    path: "/planejamento-comercial/*",
    element: (
      <LayoutWrapper>
        <RoutesIndexBusinessPlanning />
      </LayoutWrapper>
    ),
  },
  {
    path: `${PRICE_MARKDOWN_LEGACY_PREFIX}/*`,
    element: <PriceMarkdownLegacyRedirect />,
  },
  {
    path: "/remarcacao-de-preco/*",
    element: (
      <LayoutWrapper>
        <RoutesIndexRemarcacaoDePreco />
      </LayoutWrapper>
    ),
  },
  {
    path: "/informations",
    element: (
      <LazyLoadErrorBoundary>
        <Suspense fallback={<LoadingSpinner />}>
          <Informations />
        </Suspense>
      </LazyLoadErrorBoundary>
    ),
  },
  {
    path: "/login",
    element: (
      <LazyLoadErrorBoundary>
        <Suspense fallback={<LoadingSpinner />}>
          <SignIn />
        </Suspense>
      </LazyLoadErrorBoundary>
    ),
  },
  {
    path: "/conciliacao/redirect",
    element: (
      <LazyLoadErrorBoundary>
        <Suspense fallback={<LoadingSpinner />}>
          <Redirect />
        </Suspense>
      </LazyLoadErrorBoundary>
    ),
  },
  {
    path: "/users",
    element: (
      <LayoutWrapper>
        <UserPage />
      </LayoutWrapper>
    ),
  },
  {
    path: "/groups",
    element: (
      <LayoutWrapper>
        <GroupsPage />
      </LayoutWrapper>
    ),
  },
  {
    path: "/sofia",
    element: (
      <div className="w-screen h-screen">
        <iframe
          src={
            "https://widget-painel.rosana.verzel.com.br/widget/b7bec0be-9ae8-4159-abf7-f5dd9884f0cb/iframe"
          }
          className="w-full h-full"
        ></iframe>
      </div>
    ),
  },
  {
    path: "*",
    element: <PageError status={400} message="PÁGINA NÃO ENCONTRADA" />,
  },
]);

export const GlobalRoutes = () => (
  <QueryClientProvider client={queryClient}>
    <LazyLoadErrorBoundary
      key={location.pathname}
      fallback={
        <PageError
          status={500}
          message="Erro inesperado. Por favor, tente novamente ou faça login novamente."
        />
      }
    >
      <Suspense fallback={<LoadingSpinner />}>
        <RouterProvider router={router} fallbackElement={<LoadingSpinner />} />
      </Suspense>
    </LazyLoadErrorBoundary>
  </QueryClientProvider>
);

export default GlobalRoutes;
